import type { TFunction } from 'i18next';
import { IOrder } from '../interfaces/Order';
import { IStore } from '../interfaces/Store';
import { getOrderByIdV2 } from '../services/order.service';
import { showNotification } from './Notification/Notification';
import { tryPrintTicket } from './ticketHelper';

export type CompletionOrderReference =
  | string
  | { sequence: string; storeId: string };

type FetchOrderById = (orderId: string) => Promise<IOrder | undefined>;
type FetchOrderBySequence = (
  sequence: string,
  storeId: string,
) => Promise<IOrder | undefined>;

type CompletionAutoPrintDependencies = {
  fetchOrderById?: FetchOrderById;
  fetchOrderBySequence?: FetchOrderBySequence;
  printTicket?: typeof tryPrintTicket;
  t?: TFunction | ((key: string) => string);
  companyLogoUrl?: string;
  companyTaxInfo?: { name?: string; legal_name?: string; tax_system?: string };
  onError?: (error: unknown) => void;
};

const fetchPopulatedOrderById: FetchOrderById = async (orderId) => {
  const response = await getOrderByIdV2(orderId, { fetchSection: 'true' });
  return response?.data?.data as IOrder | undefined;
};

const getPopulatedStore = (order: IOrder): IStore | undefined =>
  order.createdIn && typeof order.createdIn === 'object'
    ? order.createdIn
    : undefined;

export const createWebCompletionTicketAutoPrinter = ({
  fetchOrderById = fetchPopulatedOrderById,
  fetchOrderBySequence,
  printTicket = tryPrintTicket,
  t = (key: string) => key,
  companyLogoUrl = '',
  companyTaxInfo,
  onError,
}: CompletionAutoPrintDependencies = {}) => {
  return async (
    reference: CompletionOrderReference,
    knownAutoPrintEnabled?: boolean,
  ): Promise<void> => {
    if (knownAutoPrintEnabled === false) return;

    try {
      const finalOrder = typeof reference === 'string'
        ? await fetchOrderById(reference)
        : await fetchOrderBySequence?.(reference.sequence, reference.storeId);
      if (!finalOrder) {
        throw new Error('Completed order could not be fetched for ticket printing');
      }

      const store = getPopulatedStore(finalOrder);
      if (store?.orderPageConfig?.printTicketAfterOrderCompletion !== true) return;

      await printTicket(
        finalOrder,
        store,
        t as TFunction,
        'ticketStructure',
        companyLogoUrl,
        true,
        companyTaxInfo,
      );
    } catch (error) {
      if (onError) {
        onError(error);
        return;
      }

      console.warn('[completion-ticket-auto-print]', error);
      showNotification({
        type: 'Error',
        title: String(t('Notifications:completionTicketAutoPrintFailedTitle')),
        message: String(t('Notifications:completionTicketAutoPrintFailedMessage')),
      });
    }
  };
};
